import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, forwardRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { switchMap, take, tap } from 'rxjs';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

import { PartnersService } from '@hm/core/services/partners.service';

import { ModalActionTypes } from '@lib/core/models/modal-action-types.enum';
import { BasePartnerSearchModel, LocationV2, PartnerScopedSearchModelV2, PartnerV2 } from '@lib/core/models/partners.interface';
import { AppointmentServiceCode, PrescriptionTypes } from '@lib/core/models/prescription.interface';
import { AppointmentEventPlaceType, Visit, VisitStatusTypes } from '@lib/core/models/visit.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

interface AppointmentControls {
  prescriptionsCodes: FormControl<AppointmentServiceCode[]>;
  // TODO: Remove next line when sure that prescriptions ids array is obsolete.
  prescriptionIds: FormControl<string[]>;
  appointmentType: FormControl<PrescriptionTypes | null>;
  name: FormControl<string | null>;
  price: FormControl<number | null>;
  doctorName: FormControl<string | null>;
  dateStart: FormControl<Date | null>;
  time: FormControl<string | null>;
  partnerId: FormControl<string | null>;
  partnerName: FormControl<string | null>;
  locationId: FormControl<string | null>;
  location: FormControl<string | null>;
  appointmentEventPlaceType: FormControl<AppointmentEventPlaceType | null>;
  appointmentCompletedFlag: FormControl<boolean | null>;
}

interface AppointmentValue {
  prescriptionsCodes: AppointmentServiceCode[];
  prescriptionIds: string[];
  prescriptionType: PrescriptionTypes | null;
  name: string | null;
  price: number | null;
  doctorName: string | null;
  dateStart: Date | null;
  time: string | null;
  partnerId: string | null;
  partnerName: string | null;
  locationId: string | null;
  location: string | null;
  appointmentEventPlaceType: AppointmentEventPlaceType | null;
  appointmentCompletedFlag: boolean | null;
}

@Component({
  selector: 'hm-manual-appointment-form',
  imports: [
    MatCheckbox,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatInput,
    MatLabel,
    MatSelect,
    MatSuffix,
    ReactiveFormsModule,
    MatOptionModule,
    LoaderComponent,
    MatError,
  ],
  templateUrl: './manual-appointment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ManualAppointmentFormComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => ManualAppointmentFormComponent), multi: true },
  ],
})
export class ManualAppointmentFormComponent implements OnInit, ControlValueAccessor, Validator {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly partnersService = inject(PartnersService);

  private onTouched!: () => void;
  private onChange!: (value: Partial<AppointmentValue> | null) => void;

  readonly type = input.required<ModalActionTypes>();
  readonly action = input<Visit | null>(null);
  readonly appointment = input<Visit | null>(null);

  protected appointmentForm: FormGroup<AppointmentControls>;

  protected readonly loading = signal<boolean>(true);
  protected readonly allPartnersOptions = signal<PartnerV2[]>([]);
  protected readonly allLocationsOptions = signal<LocationV2[]>([]);

  protected get fc(): AppointmentControls {
    return this.appointmentForm?.controls;
  }

  ngOnInit() {
    this.initForm();
  }

  protected selectPartnerOption(partnerId: string): void {
    this.clearPartnerRelatedControls();
    this.fc.partnerId.setValue(partnerId);
    this.searchPartnersLocations(partnerId).subscribe();
  }

  protected selectLocationOption(locationId: string): void {
    this.fc.locationId.setValue(locationId);
  }

  private searchPartners() {
    const searchRequest: BasePartnerSearchModel = {
      pageSize: 500,
      active: true,
      searchTerm: '',
    };

    return this.partnersService.searchPartnersV2(searchRequest).pipe(
      tap(result => this.allPartnersOptions.set(result.items)),
      take(1)
    );
  }

  private searchPartnersLocations(partnerId: string) {
    const searchRequest: PartnerScopedSearchModelV2 = {
      partnerId: partnerId,
      pageSize: 500,
      active: true,
    };

    return this.partnersService.searchPartnerLocationsV2(partnerId, searchRequest).pipe(
      tap(locations => this.allLocationsOptions.set(locations.items)),
      take(1)
    );
  }

  private clearPartnerRelatedControls(): void {
    this.fc.locationId.setValue(null, { emitEvent: false });
    this.fc.location.setValue(null, { emitEvent: false });
    this.allLocationsOptions.set([]);
  }

  private initForm(): void {
    const prescriptions = this.action()?.prescriptions || this.appointment()?.prescriptions || [];
    this.appointmentForm = this.fb.group<AppointmentControls>({
      prescriptionsCodes: this.fb.control(prescriptions.map(prescription => ({ code: prescription.serviceCode, name: prescription.name }))),
      prescriptionIds: this.fb.control(prescriptions.map(prescription => prescription.id)),
      appointmentType: this.fb.control(this.type() === ModalActionTypes.Create ? this.action().metadata.type : this.appointment().metadata.type, [Validators.required]),
      name: this.fb.control(this.type() === ModalActionTypes.Create ? this.action().metadata.name : this.appointment().metadata.name, [Validators.required]),
      price: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().price, [Validators.required]),
      doctorName: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.doctor_name, [Validators.required]),
      dateStart: this.fb.control(this.type() === ModalActionTypes.Create ? null : new Date(this.appointment().metadata.appointment_full_date), [Validators.required]),
      time: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.getTime(new Date(this.appointment().metadata.appointment_full_date)), [Validators.required]),
      partnerId: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().partnerId, [Validators.required]),
      partnerName: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.partner_name, [Validators.required]),
      locationId: this.fb.control(this.type() === ModalActionTypes.Create ? null : null, [Validators.required]),
      location: this.fb.control(this.type() === ModalActionTypes.Create ? null : this.appointment().metadata.location, [Validators.required]),
      appointmentEventPlaceType: this.fb.control(this.type() === ModalActionTypes.Create ? AppointmentEventPlaceType.Hospital : this.appointment().metadata.event_place, [
        Validators.required,
      ]),
      appointmentCompletedFlag: this.fb.control(this.type() === ModalActionTypes.Create ? false : this.appointment().status === VisitStatusTypes.Completed),
    });

    switch (this.type()) {
      case ModalActionTypes.Create:
        this.searchPartners().subscribe(() => this.loading.set(false));
        break;
      case ModalActionTypes.Edit:
        this.watchFormChanges();
        this.loading.set(false);
        break;
      case ModalActionTypes.Delete:
        this.loading.set(false);
    }
  }

  private initAsyncEditData() {
    const currentPartnerId = this.appointment().partnerId;
    const currentLocationId = this.appointment().systemMetadata.location_id;

    return this.searchPartnersLocations(currentPartnerId).pipe(
      switchMap(() => this.searchPartners()),
      tap(partners => {
        const currentPartner = partners.items.find(partner => partner.id === currentPartnerId);
        if (currentPartner) {
          this.fc.partnerId.setValue(currentPartner.id, { emitEvent: false });
          this.fc.partnerName.setValue(currentPartner.name, { emitEvent: false });
        }
      }),
      switchMap(() => this.searchPartnersLocations(currentPartnerId)),
      tap(locations => {
        const currentLocation = locations.items.find(location => location.id === currentLocationId && location.active);
        if (currentLocation) {
          this.fc.locationId.setValue(currentLocation.id, { emitEvent: false });
          this.fc.location.setValue(currentLocation.location.address, { emitEvent: false });
        }
      }),
      take(1)
    );
  }

  private watchFormChanges(): void {
    this.appointmentForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => this.onChange(value));
    this.appointmentForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onTouched());
  }

  private getTime(date: Date): string {
    const hours = date.getHours() < 10 ? '0' + date.getHours() : date.getHours().toString();
    const minutes = date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes();

    return `${hours}:${minutes}`;
  }

  writeValue(value: unknown): void {
    if (this.appointmentForm && value) {
      this.appointmentForm.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.appointmentForm.disable();
    else this.appointmentForm.enable();
  }

  validate(): ValidationErrors | null {
    return this.appointmentForm.valid ? null : { invalidAppointmentForm: true };
  }
}
